import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import PageShell from "../components/PageShell";
import { websitePackages, BuyModal } from "../components/MarketingPackages";
import { Check, FileText, ChevronLeft, CalendarClock, Wrench, Globe } from "lucide-react";

const HOSTING = 150;

export default function WebsitePackage() {
  const { slug } = useParams();
  const pkg = websitePackages.find((p) => p.slug === slug);
  const [open, setOpen] = useState(false);

  if (!pkg) {
    return (
      <PageShell title="Package not found">
        <div className="text-center pb-20"><a href="/#packages" className="link-peach" data-testid="wp-not-found-back">See all packages</a></div>
      </PageShell>
    );
  }

  const dueToday = pkg.buildFee + HOSTING;
  const invoicePkg = { ...pkg, price: `$${dueToday.toLocaleString()}`, cadence: "due today", secondary: `${pkg.price} build + $${HOSTING} first year` };
  const others = websitePackages.filter((p) => p.slug !== slug);

  return (
    <PageShell kicker="Website package" title={pkg.name} subtitle={pkg.tagline}>
      <section className="max-w-6xl mx-auto px-5 lg:px-8 pb-20 grid grid-cols-1 lg:grid-cols-5 gap-10">
        <div className="lg:col-span-3">
          <a href="/#packages" className="text-sm text-[color:var(--dms-navy)]/60 hover:text-[color:var(--dms-navy)] inline-flex items-center gap-1" data-testid="wp-back-link"><ChevronLeft className="w-4 h-4" /> All packages</a>
          <h2 className="font-serif text-2xl text-[color:var(--dms-navy)] mt-6">What's included</h2>
          <ul className="mt-5 space-y-3" data-testid="wp-details-list">
            {pkg.details.map((d) => (
              <li key={d} className="flex items-start gap-3">
                <span className="w-5 h-5 rounded-full bg-[color:var(--dms-peach-light)]/70 flex items-center justify-center flex-shrink-0 mt-0.5"><Check className="w-3 h-3 text-[color:var(--dms-navy)]" /></span>
                <span className="text-[color:var(--dms-navy)]/80">{d}</span>
              </li>
            ))}
          </ul>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Fact icon={Globe} title={`${pkg.price} one-time`} sub="To design and build your site" />
            <Fact icon={CalendarClock} title={`$${HOSTING} / year`} sub="Keeps your site online, secure and up to date" />
            <Fact icon={Wrench} title="$49.99 / month" sub="Optional, for changes after your site is live" testid="wp-change-fee" />
          </div>
        </div>
        <aside className="lg:col-span-2">
          <div className="bg-[color:var(--dms-navy)] text-white rounded-3xl p-8 lg:sticky lg:top-28" data-testid="wp-price-card">
            <p className="kicker text-[color:var(--dms-peach)]">Due today</p>
            <p className="font-serif text-5xl mt-3" data-testid="wp-due-today">${dueToday.toLocaleString()}</p>
            <div className="mt-5 space-y-2 text-sm text-white/75">
              <Row label="One-time build" value={pkg.price} />
              <Row label="First year of hosting" value={`$${HOSTING}`} />
              <Row label="Each year after" value={`$${HOSTING}`} />
              <Row label="Changes after launch" value="$49.99 / mo" />
            </div>
            <button onClick={() => setOpen(true)} data-testid="wp-request-invoice" className="mt-7 w-full inline-flex items-center justify-center gap-2 py-3 rounded-full text-sm font-semibold bg-[color:var(--dms-peach)] text-[color:var(--dms-navy)] hover:bg-[color:var(--dms-peach-light)] transition-colors">
              <FileText className="w-4 h-4" /> Request invoice
            </button>
            <p className="mt-3 text-[11px] text-white/55 text-center">Pay by Zelle. We start as soon as payment arrives.</p>
          </div>
          <div className="mt-6 text-sm text-[color:var(--dms-navy)]/70">
            Compare: {others.map((o, i) => (
              <span key={o.slug}>{i > 0 && " · "}<Link to={`/websites/${o.slug}`} className="link-peach" data-testid={`wp-compare-${o.slug}`}>{o.name}</Link></span>
            ))}
          </div>
        </aside>
      </section>
      <Showcase pkg={pkg} />
      {open && <BuyModal pkg={invoicePkg} onClose={() => setOpen(false)} />}
    </PageShell>
  );
}

function Showcase({ pkg }) {
  return (
    <section className="bg-[color:var(--dms-cream-2)]/50 py-16" data-testid="wp-showcase">
      <div className="max-w-6xl mx-auto px-5 lg:px-8">
        <p className="kicker">Example sites</p>
        <h2 className="font-serif text-2xl md:text-3xl text-[color:var(--dms-navy)] mt-3">What a {pkg.name} site looks like</h2>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          {pkg.examples.map((ex, i) => (
            <article key={ex.name} className="bg-white rounded-2xl overflow-hidden border border-[color:var(--dms-navy)]/10 hover-lift" data-testid={`wp-example-${i}`}>
              <div className="aspect-[16/10] overflow-hidden bg-[color:var(--dms-cream-2)]">
                <img src={ex.img} alt={`${ex.name} website`} className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="p-5">
                <p className="font-serif text-xl text-[color:var(--dms-navy)]">{ex.name}</p>
                <p className="text-sm text-[color:var(--dms-navy)]/60 mt-1">{ex.type} · {pkg.name} package</p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function Fact({ icon: Icon, title, sub, testid }) {
  return (
    <div className="bg-white rounded-2xl border border-[color:var(--dms-navy)]/10 p-5" data-testid={testid}>
      <Icon className="w-5 h-5 text-[color:var(--dms-peach)]" />
      <p className="font-serif text-lg text-[color:var(--dms-navy)] mt-3">{title}</p>
      <p className="text-xs text-[color:var(--dms-navy)]/60 mt-1">{sub}</p>
    </div>
  );
}

function Row({ label, value }) {
  return <div className="flex justify-between border-b border-white/10 pb-2"><span>{label}</span><span className="text-white">{value}</span></div>;
}
